/**
 * Widget Registry
 * 
 * Central source of truth for all widgets in Mabel.
 * Each widget can appear in one or more domains.
 */

export const WIDGETS = {
  // ============================================
  // HUB WIDGETS
  // ============================================
  
  stateCards: {
    id: 'stateCards',
    name: 'My State',
    description: 'Track mood, energy, focus, space',
    icon: 'Sparkles',
    component: 'StateCards',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 1,
    size: 'full', // full width
  },
  
  todaysFocus: {
    id: 'todaysFocus',
    name: "Today's Focus",
    description: 'Priority tasks for today',
    icon: 'Target',
    component: 'TodaysFocus',
    defaultDomain: 'hub',
    availableIn: ['hub', 'tasks'],
    defaultEnabled: true,
    defaultOrder: 2,
    size: 'half',
  },
  
  askMabel: {
    id: 'askMabel',
    name: 'Ask Mabel',
    description: 'Chat with your life coach',
    icon: 'MessageCircle',
    component: 'GuidancePrompt',
    defaultDomain: 'hub',
    availableIn: ['hub', 'tasks', 'kitchen'],
    defaultEnabled: true,
    defaultOrder: 3,
    size: 'half',
  },
  
  tasksCard: {
    id: 'tasksCard',
    name: 'Tasks',
    description: 'Your task list',
    icon: 'CheckSquare',
    component: 'TasksCard',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 4,
    size: 'half',
  },
  
  kitchenCard: {
    id: 'kitchenCard',
    name: 'Kitchen',
    description: "What's cooking?",
    icon: 'UtensilsCrossed',
    component: 'KitchenCard',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 5,
    size: 'half',
  },
  
  roadmapCard: {
    id: 'roadmapCard',
    name: 'Roadmap',
    description: 'Launch progress',
    icon: 'Map',
    component: 'RoadmapCard',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 5,
    size: 'half',
  },
  
  recentWins: {
    id: 'recentWins',
    name: 'Recent Wins',
    description: 'Celebrate progress',
    icon: 'Trophy',
    component: 'RecentWins',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 6,
    size: 'full',
  },
  
  review: {
    id: 'review',
    name: 'Review Queue',
    description: 'Facts to review',
    icon: 'ClipboardCheck',
    component: 'ReviewCard',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 7,
    size: 'full',
  },
  
  install: {
    id: 'install',
    name: 'Install App',
    description: 'Add to home screen',
    icon: 'Download',
    component: 'InstallPrompt',
    defaultDomain: 'hub',
    availableIn: ['hub'],
    defaultEnabled: true,
    defaultOrder: 8,
    size: 'full',
  },

  // ============================================
  // KITCHEN DASHBOARD CARDS
  // ============================================
  
  tonightCard: {
    id: 'tonightCard',
    name: 'Tonight',
    description: "What's for dinner",
    icon: 'Calendar',
    component: 'TonightCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 1,
    size: 'half',
  },
  
  expiringCard: {
    id: 'expiringCard',
    name: 'Freshness',
    description: 'Items expiring soon',
    icon: 'AlertTriangle',
    component: 'ExpiringCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 2,
    size: 'half',
  },
  
  shoppingCard: {
    id: 'shoppingCard',
    name: 'Shopping',
    description: 'Your shopping list',
    icon: 'ShoppingCart',
    component: 'ShoppingCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 3,
    size: 'half',
  },
  
  pantryCard: {
    id: 'pantryCard',
    name: 'Pantry',
    description: 'Your ingredients',
    icon: 'Package',
    component: 'PantryCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 4,
    size: 'half',
  },
  
  mealsCard: {
    id: 'mealsCard',
    name: 'Meal Ideas',
    description: 'Recipe suggestions',
    icon: 'ChefHat',
    component: 'MealsCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 5,
    size: 'half',
  },
  
  foodLogCard: {
    id: 'foodLogCard',
    name: 'Food Log',
    description: 'Track what you ate',
    icon: 'Utensils',
    component: 'FoodLogCardWidget',
    defaultDomain: 'kitchen',
    availableIn: ['kitchen'],
    defaultEnabled: true,
    defaultOrder: 6,
    size: 'half',
  },

  // ============================================
  // KITCHEN FULL WIDGETS (for sub-views)
  // ============================================
  
  pantry: {
    id: 'pantry',
    name: 'Pantry',
    description: 'Your ingredients',
    icon: 'Package',
    component: 'PantryWidget',
    defaultDomain: 'kitchen',
    availableIn: ['hub'],
    defaultEnabled: false,
    defaultOrder: 10,
    size: 'full',
  },
  
  shopping: {
    id: 'shopping',
    name: 'Shopping List',
    description: 'What to buy',
    icon: 'ShoppingCart',
    component: 'ShoppingWidget',
    defaultDomain: 'kitchen',
    availableIn: ['hub'],
    defaultEnabled: false,
    defaultOrder: 11,
    size: 'full',
  },
  
  meals: {
    id: 'meals',
    name: 'Meal Ideas',
    description: 'Recipe suggestions',
    icon: 'ChefHat',
    component: 'MealsWidget',
    defaultDomain: 'kitchen',
    availableIn: ['hub'],
    defaultEnabled: false,
    defaultOrder: 12,
    size: 'full',
  },
  
  mealPlan: {
    id: 'mealPlan',
    name: 'Meal Plan',
    description: 'Weekly meals',
    icon: 'Calendar',
    component: 'MealPlanWidget',
    defaultDomain: 'kitchen',
    availableIn: ['hub'],
    defaultEnabled: false,
    defaultOrder: 13,
    size: 'full',
  },
  
  foodLog: {
    id: 'foodLog',
    name: 'Food Log',
    description: 'Track what you ate',
    icon: 'Utensils',
    component: 'FoodLogWidget',
    defaultDomain: 'kitchen',
    availableIn: ['hub'],
    defaultEnabled: false,
    defaultOrder: 14,
    size: 'full',
  },

  // ============================================
  // TASKS WIDGETS
  // ============================================
  
  taskFilters: {
    id: 'taskFilters',
    name: 'Filters',
    description: 'Filter your tasks',
    icon: 'Filter',
    component: 'TaskFiltersWidget',
    defaultDomain: 'tasks',
    availableIn: ['tasks'],
    defaultEnabled: true,
    defaultOrder: 1,
    size: 'full',
  },
  
  taskList: {
    id: 'taskList',
    name: 'Task List',
    description: 'All your tasks',
    icon: 'List',
    component: 'TaskListWidget',
    defaultDomain: 'tasks',
    availableIn: ['tasks'],
    defaultEnabled: true,
    defaultOrder: 2,
    size: 'full',
  },

  // ============================================
  // FUTURE WIDGETS (placeholders)
  // ============================================
  
  goals: {
    id: 'goals',
    name: 'Goals',
    description: 'Your values and dreams',
    icon: 'Compass',
    component: 'GoalsWidget',
    defaultDomain: 'goals',
    availableIn: ['hub', 'goals'],
    defaultEnabled: false,
    defaultOrder: 1,
    size: 'full',
  },
  
  sleep: {
    id: 'sleep',
    name: 'Sleep',
    description: 'Rest tracking',
    icon: 'Moon',
    component: 'SleepWidget',
    defaultDomain: 'wellbeing',
    availableIn: ['hub', 'wellbeing'],
    defaultEnabled: false,
    defaultOrder: 1,
    size: 'half',
  },
  
  hydration: {
    id: 'hydration',
    name: 'Hydration',
    description: 'Water intake',
    icon: 'Droplets',
    component: 'HydrationWidget',
    defaultDomain: 'wellbeing',
    availableIn: ['hub', 'wellbeing'],
    defaultEnabled: false,
    defaultOrder: 2,
    size: 'half',
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get widgets available for a specific domain
 */
export const getWidgetsForDomain = (domain) => {
  return Object.values(WIDGETS).filter(
    widget => widget.availableIn.includes(domain)
  );
};

/**
 * Get default enabled widgets for a domain
 */
export const getDefaultWidgetsForDomain = (domain) => {
  return Object.values(WIDGETS)
    .filter(widget => 
      widget.defaultDomain === domain && 
      widget.defaultEnabled
    )
    .sort((a, b) => a.defaultOrder - b.defaultOrder)
    .map(widget => widget.id);
};

/**
 * Get a widget by ID
 */
export const getWidget = (widgetId) => {
  return WIDGETS[widgetId] || null;
};
